import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import LoadingSpinner from './Popup/LoadingSpinner';
import './RiskScoreLineChart.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const parseIfJsonString = (value) => {
  if (value == null) return value;
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const getMatrixMaxScore = (config) => {
  if (!config) return null;

  const matrixValues = parseIfJsonString(config.matrix_values);
  if (Array.isArray(matrixValues)) {
    let max = null;
    for (const row of matrixValues) {
      if (!Array.isArray(row)) continue;
      for (const v of row) {
        const n = Number(v);
        if (Number.isFinite(n)) {
          max = max == null ? n : Math.max(max, n);
        }
      }
    }
    if (max != null) return max;
  }

  const zoneThresholds = parseIfJsonString(config.zone_thresholds);
  if (zoneThresholds && typeof zoneThresholds === 'object') {
    let max = null;
    for (const range of Object.values(zoneThresholds)) {
      const n = Number(range?.max);
      if (Number.isFinite(n)) {
        max = max == null ? n : Math.max(max, n);
      }
    }
    if (max != null) return max;
  }

  return null;
};

const RiskScoreLineChart = ({ snapshots, riskMatrixConfig }) => {
  // Consistent color mapping for different field types
  const getFieldColor = useMemo(() => {
    return (fieldName, fieldLabel) => {
      // Normalize field name and label for consistent matching
      const normalizedName = fieldName.toLowerCase();
      const normalizedLabel = (fieldLabel || fieldName).toLowerCase();
      
      // Define consistent color mapping based on field type/name
      const colorMap = {
        // Overall impact score - always red
        'overall_impact': '#dc3545',
        'overall_risk': '#dc3545',
        'risk_score': '#dc3545',
        'overall': '#dc3545',
        
        // Probability fields - blue variations
        'probability': '#007bff',
        'likelihood': '#007bff',
        'chance': '#007bff',
        
        // Velocity fields - green variations  
        'velocity': '#28a745',
        'speed': '#28a745',
        'timeline': '#28a745',
        
        // Cost fields - orange variations
        'cost': '#fd7e14',
        'financial': '#fd7e14',
        'budget': '#fd7e14',
        'economic': '#fd7e14',
        
        // Schedule fields - purple variations
        'schedule': '#6f42c1',
        'time': '#6f42c1',
        'duration': '#6f42c1',
        'deadline': '#6f42c1',
        
        // Quality fields - teal variations
        'quality': '#20c997',
        'performance': '#20c997',
        'reliability': '#20c997',
        
        // Security fields - pink variations
        'security': '#e83e8c',
        'safety': '#e83e8c',
        'privacy': '#e83e8c',
        
        // Technical fields - cyan variations
        'technical': '#17a2b8',
        'technology': '#17a2b8',
        'complexity': '#17a2b8',
        
        // Reputation fields - yellow variations
        'reputation': '#ffc107',
        'brand': '#ffc107',
        'image': '#ffc107'
      };
      
      // First, try exact matches on field name
      if (colorMap[normalizedName]) {
        return colorMap[normalizedName];
      }
      
      // Then try partial matches on field name
      for (const [key, color] of Object.entries(colorMap)) {
        if (normalizedName.includes(key) || normalizedLabel.includes(key)) {
          return color;
        }
      }
      
      // For custom fields, create a deterministic color based on field name hash
      let hash = 0;
      const str = fieldName + fieldLabel;
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash + str.charCodeAt(i)) & 0xffffffff;
      }
      
      // Use hash to select from a palette of distinct colors
      const fallbackColors = [
        '#8e44ad', // Purple
        '#2c3e50', // Dark blue
        '#c0392b', // Dark red
        '#27ae60', // Green
        '#f39c12', // Orange
        '#9b59b6', // Violet
        '#34495e', // Blue gray
        '#16a085', // Teal
        '#d35400', // Carrot
        '#8b4513'  // Saddle brown
      ];
      
      return fallbackColors[Math.abs(hash) % fallbackColors.length];
    };
  }, []);

  // Transform snapshot data into chart-compatible format
  const chartData = useMemo(() => {
    if (!snapshots || snapshots.length === 0) {
      return null;
    }

    // Group snapshots by stage and take the latest one for each stage
    const snapshotsByStage = new Map();
    snapshots.forEach(snapshot => {
      const stageName = snapshot.stage_name || 'Unknown Stage';
      const existingSnapshot = snapshotsByStage.get(stageName);
      
      if (!existingSnapshot || new Date(snapshot.captured_at) > new Date(existingSnapshot.captured_at)) {
        snapshotsByStage.set(stageName, snapshot);
      }
    });
    
    // Convert back to array, maintaining chronological order
    const groupedSnapshots = Array.from(snapshotsByStage.values())
      .sort((a, b) => new Date(a.captured_at) - new Date(b.captured_at));

    // Extract x-axis labels from grouped snapshots
    const labels = groupedSnapshots.map(snapshot => {
      return snapshot.stage_name || new Date(snapshot.captured_at).toLocaleDateString();
    });

    // Overall risk score dataset - always red
    const riskScores = groupedSnapshots.map(snapshot => snapshot.risk_score || null);

    // Collect all impact fields across grouped snapshots
    const impactFieldsSet = new Set();
    groupedSnapshots.forEach(snapshot => {
      if (snapshot.field_values) {
        Object.entries(snapshot.field_values).forEach(([fieldName, fieldData]) => {
          // Include fields with numeric scores and relevant impact field names
          if (fieldData.score !== null && fieldData.score !== undefined && 
              (fieldName.includes('impact') || fieldName.includes('probability') || 
               fieldName.includes('velocity') || fieldName.includes('cost') || 
               fieldName.includes('schedule') || fieldName.includes('quality') ||
               fieldName.includes('security') || fieldName.includes('technical') ||
               fieldName.includes('reputation') || fieldName.includes('threat'))) {
            impactFieldsSet.add(fieldName);
          }
        });
      }
    });

    const impactFields = Array.from(impactFieldsSet);

    const datasets = [];

    // Overall risk score dataset - always red for consistency
    datasets.push({
      label: 'Overall Impact Score',
      data: riskScores,
      borderColor: '#dc3545', // Always red
      backgroundColor: '#dc354533', // Red with transparency
      borderWidth: 4,
      tension: 0.3,
      fill: false,
      pointBackgroundColor: '#dc3545',
      pointBorderColor: '#dc3545',
      pointBorderWidth: 2,
      pointRadius: 6,
    });

    // Impact field datasets with consistent coloring
    impactFields.forEach((fieldName) => {
      const fieldData = groupedSnapshots.map(snapshot => {
        if (snapshot.field_values && snapshot.field_values[fieldName]) {
          return snapshot.field_values[fieldName].score;
        }
        return null;
      });

      const fieldLabel = groupedSnapshots.find(s => s.field_values && s.field_values[fieldName])?.field_values[fieldName]?.label || fieldName;
      const color = getFieldColor(fieldName, fieldLabel);

      datasets.push({
        label: fieldLabel,
        data: fieldData,
        borderColor: color,
        backgroundColor: color + '33', // Add transparency
        borderWidth: 1.5,
        tension: 0.3,
        fill: false,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointBorderWidth: 1,
        pointRadius: 4,
      });
    });

    return {
      labels,
      datasets,
    };
  }, [snapshots, getFieldColor]);

  const yAxisMax = useMemo(() => {
    const matrixMax = getMatrixMaxScore(riskMatrixConfig);

    // Also account for any data that might exceed configured matrix max (defensive)
    let dataMax = null;
    if (chartData?.datasets?.length) {
      for (const dataset of chartData.datasets) {
        for (const v of dataset.data || []) {
          const n = Number(v);
          if (Number.isFinite(n)) {
            dataMax = dataMax == null ? n : Math.max(dataMax, n);
          }
        }
      }
    }

    const baseMax = Math.max(matrixMax ?? 25, dataMax ?? 0);
    return baseMax + 3;
  }, [riskMatrixConfig, chartData]);

  // Chart configuration options
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        bottom: 10,
        left: 10,
        right: 10,
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 20,
          padding: 15,
          usePointStyle: true,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (context) => {
            const dataIndex = context[0].dataIndex;
            // Use grouped snapshots for tooltip
            const snapshotsByStage = new Map();
            snapshots.forEach(snapshot => {
              const stageName = snapshot.stage_name || 'Unknown Stage';
              const existingSnapshot = snapshotsByStage.get(stageName);
              
              if (!existingSnapshot || new Date(snapshot.captured_at) > new Date(existingSnapshot.captured_at)) {
                snapshotsByStage.set(stageName, snapshot);
              }
            });
            
            const groupedSnapshots = Array.from(snapshotsByStage.values())
              .sort((a, b) => new Date(a.captured_at) - new Date(b.captured_at));
            
            const snapshot = groupedSnapshots[dataIndex];
            return snapshot.stage_name || new Date(snapshot.captured_at).toLocaleDateString();
          },
          label: (context) => {
            const dataIndex = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            // Use grouped snapshots for tooltip
            const snapshotsByStage = new Map();
            snapshots.forEach(snapshot => {
              const stageName = snapshot.stage_name || 'Unknown Stage';
              const existingSnapshot = snapshotsByStage.get(stageName);
              
              if (!existingSnapshot || new Date(snapshot.captured_at) > new Date(existingSnapshot.captured_at)) {
                snapshotsByStage.set(stageName, snapshot);
              }
            });
            
            const groupedSnapshots = Array.from(snapshotsByStage.values())
              .sort((a, b) => new Date(a.captured_at) - new Date(b.captured_at));
            
            const snapshot = groupedSnapshots[dataIndex];
            const dataset = context.chart.data.datasets[datasetIndex];
            const value = context.parsed.y;

            if (datasetIndex === 0) {
              // Overall risk score
              return `${dataset.label}: ${value}`;
            } else {
              // Impact field
              const fieldName = Object.keys(snapshot.field_values || {}).find(key => {
                const fieldData = snapshot.field_values[key];
                return fieldData.label === dataset.label;
              });
              
              if (fieldName && snapshot.field_values && snapshot.field_values[fieldName]) {
                const fieldData = snapshot.field_values[fieldName];
                return `${dataset.label}: ${value} (${fieldData.value})`;
              }
              return `${dataset.label}: ${value}`;
            }
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Timeline',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Impact Score',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        beginAtZero: false,
        min: 0,
        max: yAxisMax,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  }), [snapshots, yAxisMax]);

  // Early returns for error cases
  if (!snapshots) {
    return <LoadingSpinner />;
  }

  if (!Array.isArray(snapshots) || snapshots.length === 0) {
    return (
      <div className="risk-score-chart-container">
        <h4>Impact Score Progression</h4>
        <div className="chart-no-data">
          No risk assessment data available for charting.
        </div>
      </div>
    );
  }

  // Validate that snapshots have required fields
  const validSnapshots = snapshots.filter(snapshot => 
    snapshot.risk_score !== null && snapshot.risk_score !== undefined
  );

  if (validSnapshots.length === 0) {
    return (
      <div className="risk-score-chart-container">
        <h4>Impact Score Progression</h4>
        <div className="chart-error">
          Unable to display chart: No valid Impact score data found.
        </div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="risk-score-chart-container">
        <h4>Impact Score Progression</h4>
        <div className="chart-error">
          Unable to process chart data. Please check your data format.
        </div>
      </div>
    );
  }

  return (
    <div className="risk-score-chart-container">
      <h4>Impact Score Progression</h4>
      <div className="chart-wrapper">
        <Line 
          data={chartData} 
          options={chartOptions}
          aria-label="Impact score progression over time showing overall impact score and individual impact field scores"
        />
      </div>
    </div>
  );
};

export default RiskScoreLineChart;